import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { COMPLIANCE_LABEL, complianceClasses, fmtDate } from "@/lib/format";
import { AlertTriangle } from "lucide-react";

export default function Compliance() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/compliance").then(({ data }) => setRows(data)).catch(() => {});
  }, []);

  const pending = rows.filter((r) => r.compliance === "overdue" || r.compliance === "never_visited");

  return (
    <div className="p-5 md:p-10 max-w-[1400px] rise" data-testid="compliance-page">
      <p className="label-tiny">Monthly monitoring</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter">Compliance report</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Assets are expected to have a fresh geotagged capture every 30 days. Anything beyond 60 days
        is flagged overdue.
      </p>

      {pending.length > 0 && (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 flex items-start gap-3" data-testid="compliance-alert">
          <AlertTriangle size={19} className="text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900">{pending.length} asset(s) need a field visit now</p>
            <p className="mt-1 text-xs text-red-700">
              {pending.slice(0, 4).map((p) => p.name).join(", ")}
              {pending.length > 4 ? ` +${pending.length - 4} more` : ""}
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="compliance-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Asset", "Location", "Executive", "Last visit", "Visits", "Status"].map((h) => (
                <th key={h} className="px-5 py-3.5 label-tiny whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`compliance-row-${r.id}`} className="hover:bg-muted/50 transition-colors duration-150">
                <td className="px-5 py-4">
                  <Link to={`/parcels/${r.id}`} className="font-semibold hover:text-primary transition-colors duration-200">
                    {r.name}
                  </Link>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{r.survey_number}</p>
                </td>
                <td className="px-5 py-4 text-xs text-muted-foreground">
                  {[r.village, r.district].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="px-5 py-4 text-xs">{r.assigned_executive?.name || "Unassigned"}</td>
                <td className="px-5 py-4 text-xs font-mono whitespace-nowrap">{fmtDate(r.last_visit_at)}</td>
                <td className="px-5 py-4 text-xs font-mono">{r.visit_count}</td>
                <td className="px-5 py-4">
                  <span className={`rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${complianceClasses(r.compliance)}`}>
                    {COMPLIANCE_LABEL[r.compliance]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="p-10 text-sm text-muted-foreground text-center">No assets to report on yet.</p>
        )}
      </div>
    </div>
  );
}
